import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

const navItems = [
  { label: 'Home', path: '/' },
  { 
    label: 'Skincare', 
    path: '/skincare',
    submenu: [
      { label: 'Morning Routines', path: '/skincare/morning-routines' },
      { label: 'Evening Routines', path: '/skincare/evening-routines' },
      { label: 'Acne', path: '/skincare/acne' },
      { label: 'Dark Spots & Hyperpigmentation', path: '/skincare/dark-spots-hyperpigmentation' },
      { label: 'Sensitive Skin', path: '/skincare/sensitive-skin' },
    ]
  },
  { 
    label: 'Makeup', 
    path: '/makeup',
    submenu: [
      { label: 'Everyday Looks', path: '/makeup/everyday' },
      { label: 'Minimal Makeup', path: '/makeup/minimal' },
      { label: 'Essentials', path: '/makeup/essentials' },
    ]
  },
  { 
    label: 'Wellness', 
    path: '/wellness',
    submenu: [
      { label: 'Supplements', path: '/wellness/supplements' },
      { label: 'Self-Care Essentials', path: '/wellness/self-care-essentials' },
      { label: 'Daily Health Picks', path: '/wellness/daily-health-picks' },
    ]
  },
  { label: 'About', path: '/about' },
];

export const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;
  const isParentActive = (item) => {
    if (item.submenu) {
      return item.submenu.some(sub => location.pathname.startsWith(sub.path)) || location.pathname === item.path;
    }
    return location.pathname === item.path;
  };

  return (
    <nav 
      data-testid="main-navigation"
      className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-stone-100"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            data-testid="nav-logo"
            className="font-heading text-xl md:text-2xl font-semibold text-[#1C1C1C] hover:text-[#3A4D39] transition-colors duration-300"
          >
            SimplyCuratedGuide
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <div key={item.path} className="relative group">
                <Link
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={`flex items-center gap-1 text-xs tracking-widest uppercase font-medium transition-colors duration-300 py-2 ${
                    isParentActive(item) ? 'text-[#3A4D39]' : 'text-[#374151] hover:text-[#3A4D39]'
                  }`}
                >
                  {item.label}
                  {item.submenu && <ChevronDown className="w-3 h-3" />}
                </Link>
                
                {/* Desktop Dropdown */}
                {item.submenu && (
                  <div className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                    <div className="bg-white border border-[#E5E5E5] shadow-lg min-w-[220px]">
                      {item.submenu.map((subItem) => (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          className={`block px-4 py-3 text-sm transition-colors duration-200 ${
                            isActive(subItem.path) 
                              ? 'bg-[#F2EBE3] text-[#3A4D39]' 
                              : 'text-[#374151] hover:bg-[#F5F5F0] hover:text-[#3A4D39]'
                          }`}
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 text-[#1C1C1C] hover:text-[#3A4D39] transition-colors duration-300"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div 
        className={`md:hidden bg-white border-t border-stone-100 overflow-hidden transition-all duration-300 ${
          mobileMenuOpen ? 'max-h-screen' : 'max-h-0'
        }`}
      >
        <div className="px-6 py-4 space-y-1">
          {navItems.map((item) => (
            <div key={item.path}>
              <div className="flex items-center justify-between">
                <Link
                  to={item.path}
                  onClick={() => !item.submenu && setMobileMenuOpen(false)}
                  className={`block py-3 text-sm tracking-wider uppercase font-medium ${
                    isParentActive(item) ? 'text-[#3A4D39]' : 'text-[#374151]'
                  }`}
                >
                  {item.label}
                </Link>
                {item.submenu && (
                  <button
                    onClick={() => setOpenSubmenu(openSubmenu === item.path ? null : item.path)}
                    className="p-2 text-[#374151]"
                    aria-label={`Toggle ${item.label} submenu`}
                  >
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openSubmenu === item.path ? 'rotate-180' : ''}`} />
                  </button>
                )}
              </div>
              
              {/* Mobile Submenu */}
              {item.submenu && openSubmenu === item.path && (
                <div className="pl-4 pb-2 space-y-1">
                  {item.submenu.map((subItem) => (
                    <Link
                      key={subItem.path}
                      to={subItem.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`block py-2 text-sm ${
                        isActive(subItem.path) ? 'text-[#3A4D39]' : 'text-[#6B7280] hover:text-[#3A4D39]'
                      }`}
                    >
                      {subItem.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
